import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarX2,
  CheckSquare2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileWarning,
  Loader2,
  MessageSquareText,
  Search,
  ShieldAlert,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { errMsg } from "@shared/api/errors";
import {
  deleteCleanupItems,
  getCleanupItems,
  getCleanupSummary,
  type CleanupFilters,
  type CleanupItem,
  type CleanupType,
} from "../api/dataCleanup";

type CategoryDefinition = {
  type: CleanupType;
  label: string;
  description: string;
  icon: typeof MessageSquareText;
  impact: string;
};

const CATEGORIES: CategoryDefinition[] = [
  {
    type: "chats",
    label: "Conversaciones de chat",
    description: "Conversaciones entre usuarios y administración que no se pueden borrar desde el chat.",
    icon: MessageSquareText,
    impact: "Se eliminan también todos los mensajes y archivos adjuntos de las conversaciones seleccionadas.",
  },
  {
    type: "justifications",
    label: "Justificaciones",
    description: "Justificaciones de fichaje, incluidas las revisadas y las que contienen documentos.",
    icon: FileWarning,
    impact: "Si una justificación tiene un documento adjunto, el archivo también se elimina.",
  },
  {
    type: "clockings",
    label: "Fichajes",
    description: "Registros de entrada y salida creados por los usuarios.",
    icon: Clock3,
    impact: "Las justificaciones vinculadas al fichaje y sus documentos también se eliminan para evitar registros huérfanos.",
  },
  {
    type: "vacations",
    label: "Solicitudes de vacaciones",
    description: "Solicitudes pendientes, aprobadas o rechazadas y su detalle de jornadas.",
    icon: CalendarX2,
    impact: "Se elimina todo el detalle de la solicitud. El saldo carry activo se restaura cuando corresponde.",
  },
];


const CATEGORY_GROUPS = [
  {
    label: "Panel de administración y Recursos Humanos",
    description: "Registros operativos compartidos entre la gestión administrativa y el área de Recursos Humanos.",
    types: ["chats", "justifications", "clockings", "vacations"] as CleanupType[],
  },
] as const;

const STATUS_OPTIONS: Partial<Record<CleanupType, Array<{ value: string; label: string }>>> = {
  justifications: [
    { value: "", label: "Todos los estados" },
    { value: "pendiente", label: "Pendiente" },
    { value: "justificada", label: "Justificada" },
    { value: "no_justificada", label: "No justificada" },
  ],
  clockings: [
    { value: "", label: "Todos los estados" },
    { value: "abierto", label: "Abierto" },
    { value: "cerrado", label: "Cerrado" },
  ],
  vacations: [
    { value: "", label: "Todos los estados" },
    { value: "pendiente", label: "Pendiente" },
    { value: "aprobado", label: "Aprobada" },
    { value: "rechazado", label: "Rechazada" },
  ],
};

function formatDate(value: string | null): string {
  if (!value) return "—";

  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(value.includes("T") || value.includes(" ")
      ? { hour: "2-digit", minute: "2-digit" }
      : {}),
  });
}

function statusClass(status: string | null): string {
  switch (status) {
    case "aprobado":
    case "justificada":
    case "cerrado":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "rechazado":
    case "no_justificada":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "pendiente":
    case "abierto":
      return "bg-amber-50 text-amber-700 border-amber-200";
    default:
      return "bg-slate-50 text-slate-600 border-slate-200";
  }
}

function statusLabel(status: string | null): string {
  if (!status) return "";

  const labels: Record<string, string> = {
    aprobado: "Aprobada",
    rechazado: "Rechazada",
    pendiente: "Pendiente",
    justificada: "Justificada",
    no_justificada: "No justificada",
    abierto: "Abierto",
    cerrado: "Cerrado",
  };

  return labels[status] ?? status;
}

export function DataCleanupPage() {
  const queryClient = useQueryClient();
  const [type, setType] = useState<CleanupType>("chats");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [allResultsSelected, setAllResultsSelected] = useState(false);
  const [firstConfirmOpen, setFirstConfirmOpen] = useState(false);
  const [secondConfirmOpen, setSecondConfirmOpen] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const filters = useMemo<CleanupFilters>(
    () => ({
      q: deferredSearch || undefined,
      from: from || undefined,
      to: to || undefined,
      status: status || undefined,
    }),
    [deferredSearch, from, to, status],
  );

  const summary = useQuery({
    queryKey: ["settings", "data-cleanup", "summary"],
    queryFn: getCleanupSummary,
  });

  const list = useQuery({
    queryKey: ["settings", "data-cleanup", type, page, filters],
    queryFn: () => getCleanupItems(type, page, filters),
  });

  const remove = useMutation({
    mutationFn: deleteCleanupItems,
    onSuccess: async (result) => {
      setFirstConfirmOpen(false);
      setSecondConfirmOpen(false);
      setSelected(new Set());
      setAllResultsSelected(false);
      setPage(1);
      setMessage({ kind: "ok", text: result.message });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings", "data-cleanup", "summary"] }),
        queryClient.invalidateQueries({ queryKey: ["settings", "data-cleanup", type] }),
      ]);
    },
    onError: (error) => {
      setFirstConfirmOpen(false);
      setSecondConfirmOpen(false);
      setMessage({ kind: "error", text: errMsg(error, "No se pudieron eliminar los registros seleccionados.") });
    },
  });

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    setAllResultsSelected(false);
    setMessage(null);
  }, [type, search, from, to, status]);

  useEffect(() => {
    setStatus("");
  }, [type]);

  const currentCategory = CATEGORIES.find((category) => category.type === type) ?? CATEGORIES[0];
  const items = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;
  const lastPage = list.data?.meta.last_page ?? 1;
  const selectedCount = allResultsSelected ? total : selected.size;
  const pageIds = items.map((item) => item.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const statusOptions = STATUS_OPTIONS[type] ?? [];

  function toggleRow(id: number): void {
    if (allResultsSelected || remove.isPending) return;

    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleCurrentPage(): void {
    if (allResultsSelected || remove.isPending) return;

    setSelected((current) => {
      const next = new Set(current);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function clearSelection(): void {
    setSelected(new Set());
    setAllResultsSelected(false);
  }

  function beginDelete(): void {
    if (selectedCount <= 0 || remove.isPending) return;
    setMessage(null);
    setFirstConfirmOpen(true);
  }

  function executeDelete(): void {
    if (remove.isPending) return;

    remove.mutate({
      type,
      ids: Array.from(selected),
      all: allResultsSelected,
      filters,
    });
  }

  const firstMessage = allResultsSelected
    ? `Has seleccionado los ${selectedCount} registros que coinciden con los filtros actuales de “${currentCategory.label}”. ${currentCategory.impact}`
    : `Vas a eliminar ${selectedCount} ${selectedCount === 1 ? "registro" : "registros"} de “${currentCategory.label}”. ${currentCategory.impact}`;

  const secondMessage = `Esta es la confirmación final. La eliminación es permanente y no dispone de deshacer desde la aplicación. Se eliminarán ${selectedCount} ${selectedCount === 1 ? "registro seleccionado" : "registros seleccionados"}.`;

  return (
    <div className="h-full min-h-0 overflow-auto px-4 py-5 sm:px-5 sm:py-6 lg:px-6 xl:px-8">
      <div className="flex w-full min-w-0 flex-col gap-6">
        <header>
          <h1 className="flex items-center gap-3 text-2xl font-black text-slate-900 sm:text-3xl">
            <Trash2 size={28} className="text-brand-700" /> Limpieza de datos
          </h1>
          <p className="mt-1 text-slate-500">
            Elimina de forma controlada registros que no disponen de borrado en sus apartados habituales.
          </p>
        </header>

        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert size={20} className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <div className="font-black text-amber-900">Zona de eliminación permanente</div>
              <p className="mt-1 text-sm leading-relaxed text-amber-800">
                Las eliminaciones de esta sección requieren dos confirmaciones consecutivas. Revisa siempre los registros seleccionados y el impacto indicado antes de continuar.
              </p>
            </div>
          </div>
        </div>

        <section className="flex min-w-0 flex-col gap-4">
          {CATEGORY_GROUPS.map((group) => {
            const categories = CATEGORIES.filter((category) => group.types.includes(category.type));

            return (
              <div key={group.label} className="min-w-0 rounded-2xl border border-slate-200/70 bg-slate-50/55 p-4 sm:p-5">
                <div className="mb-4">
                  <h2 className="text-sm font-black text-slate-900">{group.label}</h2>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{group.description}</p>
                </div>
                <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    const active = category.type === type;
                    return (
                      <button
                        key={category.type}
                        type="button"
                        onClick={() => setType(category.type)}
                        className={`min-w-0 rounded-2xl border p-4 text-left transition ${
                          active
                            ? "border-brand-300 bg-brand-50 shadow-sm"
                            : "border-slate-200 bg-white hover:border-brand-200 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${active ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-600"}`}>
                            <Icon size={19} />
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-600">
                            {summary.isLoading ? "…" : summary.data?.[category.type] ?? 0}
                          </span>
                        </div>
                        <div className="mt-3 break-words font-black text-slate-900">{category.label}</div>
                        <p className="mt-1 break-words text-xs leading-relaxed text-slate-500">{category.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>

        <section className="rounded-2xl border border-slate-200/70 bg-white shadow-soft">
          <div className="border-b border-slate-100 p-4 sm:p-5">
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-slate-900">{currentCategory.label}</h2>
                <p className="mt-1 text-sm text-slate-500">{currentCategory.impact}</p>
              </div>
              <button
                type="button"
                onClick={beginDelete}
                disabled={selectedCount === 0 || remove.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
              >
                {remove.isPending ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Eliminar seleccionados{selectedCount > 0 ? ` (${selectedCount})` : ""}
              </button>
            </div>

            <div className="mt-5 grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_170px_170px_190px_auto]">
              <label className="relative block">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por usuario, correo o detalle…"
                  className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
                />
              </label>
              <input
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                aria-label="Desde"
                className="w-full min-w-0 rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              <input
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                aria-label="Hasta"
                className="w-full min-w-0 rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              {statusOptions.length > 0 ? (
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className="w-full min-w-0 rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : <div />}
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFrom("");
                  setTo("");
                  setStatus("");
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 xl:w-auto"
              >
                <X size={15} /> Limpiar filtros
              </button>
            </div>
          </div>

          {message && (
            <div className={`mx-5 mt-5 rounded-xl border px-4 py-3 text-sm font-bold ${message.kind === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
              {message.text}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
              <span className="font-bold">{total} registros</span>
              {selectedCount > 0 && (
                <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-black text-brand-700">
                  {selectedCount} seleccionados
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {total > 0 && !allResultsSelected && (
                <button
                  type="button"
                  onClick={() => {
                    setSelected(new Set());
                    setAllResultsSelected(true);
                  }}
                  className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-black text-brand-700 transition hover:bg-brand-100"
                >
                  Seleccionar los {total} resultados
                </button>
              )}
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black text-slate-600 transition hover:bg-slate-50"
                >
                  Limpiar selección
                </button>
              )}
            </div>
          </div>

          {list.isLoading ? (
            <div className="flex items-center justify-center gap-2 px-6 py-16 text-slate-500">
              <Loader2 size={20} className="animate-spin" /> Cargando registros…
            </div>
          ) : list.isError ? (
            <div className="m-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm font-bold text-rose-700">
              {errMsg(list.error, "No se pudieron cargar los registros.")}
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-slate-50/80">
                  <tr className="border-b border-slate-100 text-left text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                    <th className="w-14 px-5 py-3">
                      <button
                        type="button"
                        onClick={toggleCurrentPage}
                        disabled={items.length === 0 || allResultsSelected}
                        aria-label="Seleccionar página"
                        className="text-slate-500 disabled:opacity-40"
                      >
                        {allResultsSelected || allPageSelected ? <CheckSquare2 size={18} className="text-brand-700" /> : <Square size={18} />}
                      </button>
                    </th>
                    <th className="px-4 py-3">Registro</th>
                    <th className="px-4 py-3">Detalle</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: CleanupItem) => {
                    const checked = allResultsSelected || selected.has(item.id);
                    return (
                      <tr key={item.id} className={`border-b border-slate-100 transition ${checked ? "bg-brand-50/45" : "hover:bg-slate-50/60"}`}>
                        <td className="px-5 py-4 align-top">
                          <button
                            type="button"
                            onClick={() => toggleRow(item.id)}
                            disabled={allResultsSelected}
                            aria-label={`Seleccionar registro ${item.id}`}
                            className="text-slate-500 disabled:opacity-70"
                          >
                            {checked ? <CheckSquare2 size={18} className="text-brand-700" /> : <Square size={18} />}
                          </button>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="font-black text-slate-900">{item.title}</div>
                          {item.subtitle && <div className="mt-1 max-w-[360px] text-xs text-slate-500">{item.subtitle}</div>}
                          <div className="mt-1 text-[11px] font-bold text-slate-400">ID {item.id}</div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="max-w-[390px] text-sm text-slate-600">{item.details || "—"}</div>
                          {item.warning && (
                            <div className="mt-2 flex max-w-[430px] items-start gap-1.5 text-xs font-bold leading-relaxed text-amber-700">
                              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {item.warning}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 align-top">
                          {item.status ? (
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${statusClass(item.status)}`}>
                              {statusLabel(item.status)}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 align-top text-slate-500">{formatDate(item.date)}</td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-14 text-center text-slate-400">
                        No hay registros para los filtros seleccionados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-4 sm:px-5">
            <span className="text-sm font-bold text-slate-500">
              Página {list.data?.meta.current_page ?? page} de {lastPage}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || list.isFetching}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                aria-label="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={page >= lastPage || list.isFetching}
                onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
                className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                aria-label="Página siguiente"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={firstConfirmOpen}
        title="Confirmar eliminación"
        message={firstMessage}
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
        destructive
        onClose={() => setFirstConfirmOpen(false)}
        onConfirm={() => {
          setFirstConfirmOpen(false);
          setSecondConfirmOpen(true);
        }}
      />

      <ConfirmDialog
        open={secondConfirmOpen}
        title="Eliminación definitiva"
        message={secondMessage}
        confirmLabel={remove.isPending ? "Eliminando…" : "Eliminar definitivamente"}
        cancelLabel="Volver"
        destructive
        onClose={() => {
          if (!remove.isPending) setSecondConfirmOpen(false);
        }}
        onConfirm={executeDelete}
      />
    </div>
  );
}
